import type { ImportOrderStatus } from '../types/import-tracking.types';

/**
 * Qué se puede hacer con una orden según su estado.
 *
 * ⚠️ **Esto es un espejo de `packages/backend/src/services/order-state-rules.ts`.**
 * El backend es la defensa real —rechaza aunque la pantalla deje apretar— y esta
 * copia existe solo para no ofrecer un botón que después va a dar error. Si
 * cambia una, cambia la otra: el síntoma de que se separaron es siempre el mismo,
 * un botón encendido que al tocarlo devuelve un mensaje de rechazo.
 *
 * Los textos se repiten a propósito y no se importan del backend: el frontend no
 * comparte código con el servidor, y un texto duplicado que se ve raro es mucho
 * más barato de detectar que un import roto en el build.
 */

/**
 * ¿Esta orden permite mover sus tramos?
 *
 * @returns `null` si se puede; si no, el motivo para mostrarle a una persona.
 */
export function motivoParaNoMoverTramos(status: ImportOrderStatus): string | null {
  if (status === 'CANCELLED') {
    return 'Esta orden está cancelada. No se pueden mover sus tramos.';
  }
  if (status === 'QUOTE' || status === 'DRAFT') {
    return 'Se habilita al confirmar la orden';
  }
  return null;
}

/**
 * ¿Se puede arrancar este tramo, mirando los que vienen antes?
 *
 * Los tramos son una cadena: el camión no puede salir de Arica antes de que el
 * barco llegue a Arica. Un tramo cancelado no frena a los siguientes.
 *
 * Solo aplica al arranque — completar un tramo en curso siempre es válido.
 */
export function motivoParaNoArrancarTramo(
  tramo: { legOrder: number; status: string },
  hermanos: Array<{ legOrder: number; status: string }>
): string | null {
  if (tramo.status !== 'PENDING') return null;

  const anterioresSinTerminar = hermanos
    .filter((t) => t.legOrder < tramo.legOrder)
    .filter((t) => t.status !== 'COMPLETED' && t.status !== 'CANCELLED')
    .sort((a, b) => a.legOrder - b.legOrder);

  const primero = anterioresSinTerminar[0];
  return primero ? `Falta completar el tramo ${primero.legOrder}` : null;
}

/**
 * ¿Se puede llevar la orden de un estado al otro?
 *
 * El menú de "Cambio directo" existe a propósito —pasa que nadie fue marcando
 * los estados y la carga ya llegó— pero no puede saltear la confirmación, que es
 * la puerta que también habilita el enlace del cliente y los documentos.
 *
 * Desde un borrador solo se sale confirmando o cancelando.
 */
export function motivoParaNoCambiarEstado(
  actual: ImportOrderStatus,
  nuevo: ImportOrderStatus
): string | null {
  if (actual !== 'QUOTE' && actual !== 'DRAFT') return null;
  if (nuevo === 'CONFIRMED' || nuevo === 'CANCELLED' || nuevo === actual) return null;
  return 'Hay que confirmar la orden antes de moverla a este estado.';
}

/**
 * ¿Se puede emitir el HBL o el AWB de esta orden?
 *
 * Un HBL es el título de la carga, no un informe. De un borrador o de una orden
 * cancelada no se emite.
 */
export function motivoParaNoEmitirDocumento(status: ImportOrderStatus): string | null {
  if (status === 'CANCELLED') {
    return 'La orden está cancelada: no se emite documento de embarque.';
  }
  if (status === 'QUOTE' || status === 'DRAFT') {
    return 'El documento de embarque se emite con la orden confirmada.';
  }
  return null;
}

/**
 * ¿Se puede editar el armado del contenedor?
 *
 * El armado de una carga ya entregada es lo que el cliente recibió. Reescribirlo
 * haría que una auditoría posterior vea un contenedor que nunca existió.
 */
export function motivoParaNoEditarArmado(status: ImportOrderStatus): string | null {
  if (status === 'DELIVERED') {
    return 'La carga ya se entregó: el armado del contenedor queda como quedó.';
  }
  if (status === 'CANCELLED') {
    return 'La orden está cancelada: no se edita el armado.';
  }
  return null;
}

/** ¿Se le puede generar el enlace público a esta orden? */
export function motivoParaNoCompartir(status: ImportOrderStatus): string | null {
  if (status === 'CANCELLED') {
    return 'La orden está cancelada: no corresponde compartir un seguimiento.';
  }
  if (status === 'QUOTE' || status === 'DRAFT') {
    return 'El enlace se habilita cuando la orden está confirmada.';
  }
  return null;
}
